/**
 * @file keyPointGroups.ts
 * @brief Meeting Intelligence key points grouped under their topic headings.
 *
 * @details
 * Since 13-10-2026 each key point carries `topic`, a broad theme of the
 * meeting. The backend (keyPointTopics.mjs) already folds one-point topics
 * and stores the points grouped: topics in the order they were discussed,
 * "Other" last, points by importance. Same rule here as in the MoM PDF and
 * the phone (FRONTEND keyPointGroups.ts):
 *   - no point has a topic → not grouped: the caller's flat list as before
 *     (every meeting made before 13-10-2026);
 *   - otherwise a heading per topic in the order the topics first appear in
 *     the stored list, points in stored order, points without a topic last
 *     under no heading.
 * Pure; never drops or reorders a point inside a group.
 *
 * @references
 * - ./components/MeetingDetailView.tsx (KeyPointsTab, phone layout Points tab)
 * - BACKEND/src/features/boardroom/keyPointTopics.mjs
 *
 * @author DES
 * @date 13-10-2026
 */

export interface TopicKeyPoint {
  point?: string | null;
  topic?: string | null;
}

export interface KeyPointGroup<T> {
  /** null = points without a topic (shown with no heading). */
  topic: string | null;
  items: T[];
}

export interface KeyPointGrouping<T> {
  grouped: boolean;
  groups: KeyPointGroup<T>[];
}

/** One trimmed line, or null. */
export function cleanTopic(raw: unknown): string | null {
  if (typeof raw !== "string") return null;
  const t = raw.replace(/\s+/g, " ").trim();
  return t || null;
}

/**
 * @param points in stored order
 * @returns the groups to render; `grouped: false` means render one flat list
 */
export function groupKeyPoints<T extends TopicKeyPoint>(points: readonly T[]): KeyPointGrouping<T> {
  const list = [...points];
  if (!list.some((kp) => cleanTopic(kp.topic))) {
    return { grouped: false, groups: list.length ? [{ topic: null, items: list }] : [] };
  }
  const byTopic = new Map<string, KeyPointGroup<T>>();
  const loose: T[] = [];
  for (const kp of list) {
    const topic = cleanTopic(kp.topic);
    if (!topic) {
      loose.push(kp);
      continue;
    }
    const key = topic.toLowerCase();
    const g = byTopic.get(key) ?? { topic, items: [] };
    g.items.push(kp);
    byTopic.set(key, g);
  }
  const groups = [...byTopic.values()];
  if (loose.length) groups.push({ topic: null, items: loose });
  return { grouped: true, groups };
}
